export interface Volume {
  number: string
  title: string
  eyebrow?: string
  subtitle: string
  intro: string[]
  flowLabel?: string
  flow?: string[]
  bulletsLabel: string
  bullets: string[]
  afterFlow?: string[]
  afterText?: string
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'MMA Foundations, Stance, Footwork & Distance Control',
    subtitle: 'Build the base that every other phase of MMA depends on.',
    intro: [
      'Before combinations, takedowns or complex transitions, athletes need a stance that allows them to move, strike, defend and react.',
      'This volume organizes the fundamentals into a clear coaching progression.',
    ],
    bulletsLabel: 'Inside:',
    bullets: [
      'MMA stance and posture',
      'Balance and base',
      'Movement patterns',
      'Footwork',
      'Angles',
      'Range management',
      'Entries and exits',
      'Defensive positioning',
      'Beginner-to-advanced progressions',
      'Visual corrections',
    ],
    result:
      'Athletes develop a stronger MMA base instead of learning techniques on unstable foundations.',
  },
  {
    number: 'Volume 02',
    title: 'MMA Striking, Defense & Combination Development',
    subtitle: 'Teach MMA-specific striking — not isolated boxing or kickboxing.',
    intro: [
      'A premium visual system for developing striking while maintaining awareness of clinch and takedown threats.',
    ],
    bulletsLabel: 'Inside:',
    bullets: [
      'Range control',
      'Punches and kicks',
      'Defensive reactions',
      'Counters',
      'Combination systems',
      'Angle changes',
      'Target selection',
      'Takedown awareness',
      'Strike-to-exit sequences',
      'Correct-vs-incorrect mechanics',
      'Visual coaching maps',
    ],
    result:
      'Athletes stop throwing random combinations and begin striking with purpose, reaction and MMA-specific decision-making.',
  },
  {
    number: 'Volume 03',
    title: 'Clinch, Cage Control & Takedown Integration',
    subtitle: 'Connect striking range to control, pressure and takedowns.',
    intro: [
      'A complete visual system for teaching the MMA clinch and cage without turning the session into isolated wrestling.',
    ],
    bulletsLabel: 'Inside:',
    bullets: [
      'Clinch entries',
      'Head position',
      'Inside control',
      'Underhooks and body locks',
      'Cage pressure',
      'Escape lanes',
      'Off-balancing',
      'Takedown entries',
      'Failed-entry recovery',
      'Defensive reactions',
      'Complete clinch-to-control chains',
    ],
    result:
      'Athletes learn how to move from striking into control and takedowns without losing position.',
  },
  {
    number: 'Volume 04',
    title: 'Ground Control, Ground-and-Pound & Submission Integration',
    subtitle: 'Teach ground MMA as a connected system.',
    intro: [
      'This volume focuses on controlling positions, creating offensive opportunities and connecting ground control with MMA-specific decisions.',
    ],
    bulletsLabel: 'Inside:',
    bullets: [
      'Positional control',
      'Pressure principles',
      'Posture',
      'Ground-and-pound integration',
      'Defensive awareness',
      'Escapes',
      'Transitions',
      'Submission opportunities',
      'Control-before-attack systems',
      'Progressive partner work',
    ],
    result:
      'Ground work becomes structured MMA coaching instead of disconnected grappling techniques.',
  },
  {
    number: 'Volume 05',
    title: 'Transitions, Scrambles & Full-Phase MMA Integration',
    subtitle: 'Teach what happens between the techniques.',
    intro: ['This is where MMA becomes MMA.'],
    flowLabel: 'Athletes learn to connect:',
    flow: ['Striking', 'Clinch', 'Takedown', 'Ground', 'Scramble', 'Stand'],
    bulletsLabel: 'Inside:',
    bullets: [
      'Full-phase transitions',
      'Scramble reactions',
      'Failed-takedown recovery',
      'Wall get-ups',
      'Return-to-standing systems',
      'Reaction maps',
      'Control-or-reset decisions',
      'Complete phase chains',
      'Coach-ready progression drills',
    ],
    result:
      'Athletes stop freezing when the position changes and start understanding how one phase creates the next.',
  },
  {
    number: 'Volume 06',
    title: '150 Ready-to-Coach MMA Sessions',
    eyebrow: 'The practical heart of the system',
    subtitle: 'Stop wondering what to teach tomorrow.',
    intro: [
      'You receive 150 complete MMA sessions organized from beginner to advanced.',
      'Sessions cover the major phases of MMA and give the coach a clear framework for:',
    ],
    bulletsLabel: '',
    bullets: [
      'Objective',
      'Setup',
      'Technical focus',
      'Partner role',
      'Progression',
      'Resistance',
      'Coaching cues',
      'Corrections',
      'Application',
      'Reset',
    ],
    afterFlow: ['Open', 'Choose', 'Coach'],
    afterText:
      'Instead of designing every session from scratch, choose the appropriate session and begin.',
    result: 'You have an organized coaching library ready whenever you need it.',
    popular: true,
  },
]
